import { Link } from "react-router-dom";
import {
  Heart,
  Star,
  Shield,
  Globe,
  Users,
  Award,
  ArrowRight,
} from "lucide-react";

const values = [
  {
    icon: Heart,
    title: "Cuidado Genuíno",
    desc: "Cada viagem é planejada com dedicação e atenção aos mínimos detalhes, como se fosse a nossa própria.",
  },
  {
    icon: Shield,
    title: "Confiança Total",
    desc: "Transparência em cada etapa do processo, com suporte completo antes, durante e após a viagem.",
  },
  {
    icon: Globe,
    title: "Expertise Global",
    desc: "Conhecimento profundo de destinos ao redor do mundo, com parceiros locais de confiança.",
  },
  {
    icon: Users,
    title: "Inclusividade",
    desc: "Celebramos a diversidade. Todos são bem-vindos aqui, especialmente a comunidade LGBTQ+.",
  },
  {
    icon: Star,
    title: "Excelência",
    desc: "Padrão 5 estrelas em atendimento, com reconhecimento consistente dos nossos clientes.",
  },
  {
    icon: Award,
    title: "Experiência",
    desc: "Mais de 10 anos transformando sonhos em realidade para milhares de viajantes brasileiros.",
  },
];

const milestones = [
  {
    year: "2013",
    title: "Fundação",
    desc: "Mahlo Viagens nasce em Santos, SP, com a missão de oferecer experiências personalizadas.",
  },
  {
    year: "2016",
    title: "Expansão",
    desc: "Ampliamos nosso portfólio para destinos internacionais e lançamos pacotes de luxo.",
  },
  {
    year: "2019",
    title: "Reconhecimento",
    desc: "Alcançamos 5.0 estrelas de avaliação e nos tornamos referência no litoral de SP.",
  },
  {
    year: "2023",
    title: "Hoje",
    desc: "Mais de 5.000 clientes satisfeitos e destinos espalhados pelos 5 continentes.",
  },
];

const About = () => (
  <div data-testid="about-page" className="pt-16 md:pt-20">
    <section className="page-hero flex items-center justify-center min-h-[350px] relative overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-40"
        style={{
          backgroundImage:
            "url(https://images.pexels.com/photos/7368311/pexels-photo-7368311.jpeg)",
        }}
      />
      <div className="relative z-10 text-center text-white px-4">
        <p className="text-[#89A6A3] font-semibold tracking-widest text-sm uppercase mb-3">
          Quem somos
        </p>
        <h1 className="font-heading text-4xl md:text-6xl font-bold">
          Sobre a Mahlo Viagens
        </h1>
        <p className="mt-4 text-white/80 text-lg max-w-xl mx-auto">
          Uma agência apaixonada por criar jornadas únicas e memoráveis.
        </p>
      </div>
    </section>

    <section data-testid="story-section" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-[#89A6A3] font-semibold tracking-widest text-sm uppercase mb-3">
              Nossa História
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#2E4E72] mb-6">
              Nascemos do amor por viajar
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-5">
              A Mahlo Viagens foi fundada em 2013 em Santos, São Paulo, por
              profissionais apaixonados por viagem e dedicados a proporcionar
              experiências únicas e inesquecíveis.
            </p>
            <p className="text-slate-600 leading-relaxed mb-5">
              Desde o início, nossa missão sempre foi clara: oferecer
              atendimento personalizado e de alta qualidade, com atenção
              especial a cada detalhe do planejamento. Acreditamos que uma
              viagem bem planejada é a base para memórias que duram a vida toda.
            </p>
            <p className="text-slate-600 leading-relaxed mb-8">
              Ao longo dos anos, construímos uma rede sólida de parceiros em
              todo o mundo, garantindo que nossos clientes recebam o melhor
              serviço onde quer que estejam.
            </p>
            <Link
              to="/contato"
              data-testid="about-contact-cta"
              className="inline-flex items-center gap-2 bg-[#89A6A3] text-[#2E4E72] font-bold px-8 py-3 rounded-full hover:bg-[#6B8C89] hover:text-white transition-all duration-300"
            >
              Fale com a Gente <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              [
                "https://images.pexels.com/photos/2724079/pexels-photo-2724079.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
                "Destino tropical",
                "",
              ],
              [
                "https://images.pexels.com/photos/587838/pexels-photo-587838.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
                "Paris",
                "mt-8",
              ],
              [
                "https://images.pexels.com/photos/4159997/pexels-photo-4159997.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
                "Alpes",
                "",
              ],
              [
                "https://images.pexels.com/photos/6580703/pexels-photo-6580703.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
                "Rio de Janeiro",
                "mt-8",
              ],
            ].map(([src, alt, cls]) => (
              <img
                key={alt}
                src={src}
                alt={alt}
                className={`rounded-2xl w-full h-52 object-cover shadow-md ${cls}`}
                loading="lazy"
              />
            ))}
          </div>
        </div>
      </div>
    </section>

    <section data-testid="timeline-section" className="py-20 bg-[#F4F7F6]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-14">
          <p className="text-[#89A6A3] font-semibold tracking-widest text-sm uppercase mb-3">
            Nossa Trajetória
          </p>
          <h2 className="font-heading text-4xl font-bold text-[#2E4E72]">
            Uma história de crescimento
          </h2>
        </div>
        <div className="relative">
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-[#B0CAC5] md:-translate-x-0.5" />
          <div className="space-y-10">
            {milestones.map((m, i) => (
              <div
                key={m.year}
                data-testid={`milestone-${m.year}`}
                className={`relative flex flex-col md:flex-row gap-6 ${i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}
              >
                <div
                  className={`md:w-1/2 ${i % 2 === 0 ? "md:text-right md:pr-12" : "md:pl-12"} pl-12 md:pl-0`}
                >
                  <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                    <span className="text-[#89A6A3] font-bold text-xl">
                      {m.year}
                    </span>
                    <h3 className="font-heading font-bold text-[#2E4E72] text-lg mt-1">
                      {m.title}
                    </h3>
                    <p className="text-slate-600 text-sm mt-2">{m.desc}</p>
                  </div>
                </div>
                <div className="absolute left-0 md:left-1/2 top-6 w-8 h-8 bg-[#89A6A3] rounded-full border-4 border-white shadow-md md:-translate-x-1/2 flex items-center justify-center">
                  <div className="w-2 h-2 bg-[#2E4E72] rounded-full" />
                </div>
                <div className="md:w-1/2" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section data-testid="values-section" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <p className="text-[#89A6A3] font-semibold tracking-widest text-sm uppercase mb-3">
            O que nos guia
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#2E4E72]">
            Nossos Valores
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {values.map(({ icon: Icon, title, desc }) => (
            <div
              key={title}
              data-testid={`value-${title.toLowerCase().replace(/\s+/g, "-")}`}
              className="flex gap-4 p-6 rounded-2xl bg-[#F4F7F6] hover:shadow-md transition-shadow duration-300"
            >
              <div className="w-12 h-12 bg-[#B0CAC5] rounded-xl flex items-center justify-center flex-shrink-0">
                <Icon size={22} className="text-[#2E4E72]" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-[#2E4E72] mb-2">
                  {title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section
      data-testid="lgbtq-section"
      className="py-20 bg-[#2E4E72] text-white"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
        <div className="rainbow-bar w-20 mx-auto mb-8" />
        <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">
          Um espaço para todos
        </h2>
        <p className="text-white/80 text-lg leading-relaxed max-w-3xl mx-auto mb-8">
          Na Mahlo Viagens, acreditamos que o amor não tem fronteiras — e as
          aventuras também não. Somos uma agência orgulhosamente inclusiva, que
          acolhe e celebra a comunidade LGBTQ+ com o mesmo respeito, cuidado e
          excelência que oferecemos a todos os nossos clientes.
        </p>
        <p className="text-white/70 leading-relaxed max-w-2xl mx-auto mb-10">
          Planejamos viagens seguras, acolhedoras e inesquecíveis para casais
          LGBTQ+, famílias, grupos e indivíduos que buscam destinos onde possam
          ser plenamente quem são.
        </p>
        <Link
          to="/contato"
          data-testid="lgbtq-cta"
          className="bg-[#89A6A3] text-[#2E4E72] font-bold px-8 py-4 rounded-full text-lg hover:bg-[#6B8C89] hover:text-white hover:scale-105 transition-all duration-300 inline-block"
        >
          Planejar Minha Viagem
        </Link>
      </div>
    </section>
  </div>
);

export default About;
